import { unwrap, type NotificationOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
  cn,
  toast,
  type NotificationTone,
} from "@sagot/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useInboxStream, type InboxStreamEvent } from "../features/engage/inbox/use-inbox-stream";
import { queryKeys } from "../lib/query-keys";
import { usePermissions } from "../session/permissions";
import { useApi } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";
import { present } from "./notification-look";

/** Inbox stream event published after a bell item was created (`InboxEventType.NOTIFIED`). */
const NOTIFIED_EVENT = "notified";

const TONE_CLASS: Record<NotificationTone, string> = { danger: "text-danger", success: "text-success", warning: "text-warning", info: "text-primary-ink" };

function useRelativeTime() {
  const { t, i18n } = useTranslation();
  const format = new Intl.RelativeTimeFormat(i18n.language === "fil" ? "fil-PH" : "en-PH", { numeric: "auto" });
  return (iso: string) => {
    const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
    if (Math.abs(minutes) < 1) return t("console.justNow");
    if (Math.abs(minutes) < 60) return format.format(minutes, "minute");
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return format.format(hours, "hour");
    return format.format(Math.round(hours / 24), "day");
  };
}

/** Topbar bell: `GET /v1/notifications`, read one on open, "Mark all read". */
export function NotificationsMenu() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { summary } = useWorkspaceContext();
  const key = queryKeys.notifications(summary.id);
  const relative = useRelativeTime();
  const list = useQuery({ queryKey: key, queryFn: () => unwrap(api.GET("/v1/notifications", { params: { query: { limit: 10 } } })), refetchInterval: 60_000 });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const readOne = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/notifications/{notification_id}/read", { params: { path: { notification_id: id } } })),
    onSettled: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/notifications/read-all")),
    onSuccess: () => toast.success(t("shell.allMarkedRead")),
    onSettled: refresh,
  });
  const canSeeInbox = usePermissions().can("inbox.view", { anywhere: true });
  // A handoff or assignment rings the bell right away; the 60 s poll stays as the fallback.
  const onInboxEvent = useCallback(
    (event: InboxStreamEvent) => {
      if (event.type === NOTIFIED_EVENT) void queryClient.invalidateQueries({ queryKey: key });
    },
    [queryClient, key],
  );
  useInboxStream(onInboxEvent, canSeeInbox);
  const unread = (list.data?.unread ?? 0) > 0;
  const open = (item: NotificationOut) => {
    if (!item.read_at) readOne.mutate(item.id);
    void navigate(item.link ?? present(item.type, summary.slug).href);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" iconOnly aria-label={unread ? `${t("shell.notifications")} (${t("shell.unread")})` : t("shell.notifications")} className="relative pointer-coarse:h-11 pointer-coarse:w-11">
          <Icon name="bell" />
          {unread ? <span aria-hidden="true" className="absolute right-2 top-2 size-2 rounded-pill bg-signal-500 ring-2 ring-surface" /> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" aria-label={t("shell.notifications")}>
        <DropdownMenuGroup className="flex items-center justify-between border-b border-line px-4 py-2">
          <DropdownMenuLabel className="p-0 text-sm font-bold text-fg">{t("shell.notifications")}</DropdownMenuLabel>
          <DropdownMenuItem
            disabled={!unread}
            className="w-auto px-2 py-1 text-xs font-semibold text-primary-ink"
            onSelect={(event) => {
              event.preventDefault();
              readAll.mutate();
            }}
          >
            {t("shell.markAllRead")}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup aria-label={t("shell.notifications")}>
          {list.isPending ? (
            <DropdownMenuItem disabled className="block space-y-2 px-4 py-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </DropdownMenuItem>
          ) : list.isError ? (
            <DropdownMenuItem disabled className="px-4 py-4 text-sm text-fg-muted">
              {t("console.notificationsError")}
            </DropdownMenuItem>
          ) : list.data.items.length === 0 ? (
            <DropdownMenuItem disabled className="flex-col px-4 py-6 text-center">
              <Icon name="check-circle" className="size-6 text-success" />
              <span className="mt-2 text-sm font-bold">{t("console.noNotificationsTitle")}</span>
              <span className="mt-1 text-xs text-fg-muted">{t("console.noNotificationsBody")}</span>
            </DropdownMenuItem>
          ) : (
            list.data.items.map((item) => {
              const look = present(item.type, summary.slug);
              return (
                <DropdownMenuItem
                  key={item.id}
                  onSelect={() => {
                    open(item);
                  }}
                  className="items-start gap-3 rounded-none border-b border-line px-4 py-3 last:border-0"
                >
                  <Icon name={look.icon} className={cn("mt-0.5", TONE_CLASS[look.tone])} />
                  <span className="min-w-0 flex-1">
                    <span className={cn("block text-sm", item.read_at ? "text-fg-muted" : "font-semibold text-fg")}>{item.title}</span>
                    <span className="mt-0.5 block text-xs text-fg-subtle">{relative(item.created_at)}</span>
                  </span>
                  {item.read_at ? null : (
                    <span className="mt-1.5 size-2 shrink-0 rounded-pill bg-primary">
                      <span className="sr-only">{t("shell.unread")}</span>
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
